/**
 * ROLE HELPERS
 *
 * Utility functions for role-based access control.
 * Handles the mapping between roles, teams, and permissions.
 */

import type { UserRole } from '@/types/database';

/**
 * Get the team from a given role
 * @param role User role
 * @returns Team name ('PLATFORM', 'IT', 'DATA', 'SYSTEM') or null
 */
export function getTeamFromRole(role: UserRole): 'PLATFORM' | 'IT' | 'DATA' | 'SYSTEM' | null {
  switch (role) {
    case 'PLATFORM_TEAM':
    case 'PLATFORM_ADMIN':
      return 'PLATFORM';
    case 'IT_TEAM':
    case 'IT_ADMIN':
      return 'IT';
    case 'DATA_TEAM':
    case 'DATA_ADMIN':
      return 'DATA';
    case 'SYSTEM_ADMIN':
      return 'SYSTEM';
    default:
      return null;
  }
}

/**
 * Check if a role is an admin role
 * @param role User role
 * @returns true if role is an admin role (IT_ADMIN, DATA_ADMIN, PLATFORM_ADMIN, or SYSTEM_ADMIN)
 */
export function isAdmin(role: UserRole): boolean {
  return role === 'IT_ADMIN' || role === 'DATA_ADMIN' || role === 'PLATFORM_ADMIN' || role === 'SYSTEM_ADMIN';
}

/**
 * Check if a role is a team admin (not SYSTEM_ADMIN)
 * @param role User role
 * @returns true if role is IT_ADMIN, DATA_ADMIN, or PLATFORM_ADMIN
 */
export function isTeamAdmin(role: UserRole): boolean {
  return role === 'IT_ADMIN' || role === 'DATA_ADMIN' || role === 'PLATFORM_ADMIN';
}

/**
 * Get the admin team from a role
 * @param role User role
 * @returns Team name if user is a team admin, null otherwise
 */
export function getAdminTeam(role: UserRole): 'PLATFORM' | 'IT' | 'DATA' | null {
  switch (role) {
    case 'PLATFORM_ADMIN':
      return 'PLATFORM';
    case 'IT_ADMIN':
      return 'IT';
    case 'DATA_ADMIN':
      return 'DATA';
    default:
      return null;
  }
}

/**
 * Check if an admin can manage a target role
 * SYSTEM_ADMIN can assign any role
 * Team admins can assign their own team roles (including admin role)
 * @param adminRole The role of the user trying to assign the role
 * @param targetRole The role being assigned
 * @returns true if the admin can assign this role
 */
export function canAdminAssignRole(adminRole: UserRole, targetRole: UserRole): boolean {
  // SYSTEM_ADMIN can assign any role
  if (adminRole === 'SYSTEM_ADMIN') {
    return true;
  }

  // Team admins can assign their own team roles (both base and admin)
  if (adminRole === 'IT_ADMIN') {
    return targetRole === 'IT_TEAM' || targetRole === 'IT_ADMIN';
  }
  if (adminRole === 'DATA_ADMIN') {
    return targetRole === 'DATA_TEAM' || targetRole === 'DATA_ADMIN';
  }
  if (adminRole === 'PLATFORM_ADMIN') {
    return targetRole === 'PLATFORM_TEAM' || targetRole === 'PLATFORM_ADMIN';
  }

  // Non-admin users cannot assign roles
  return false;
}

/**
 * Get assignable roles based on current user role
 * @param currentRole The role of the user assigning roles
 * @returns Array of roles that can be assigned
 */
export function getAssignableRoles(currentRole: UserRole): UserRole[] {
  if (currentRole === 'SYSTEM_ADMIN') {
    return ['SYSTEM_ADMIN', 'PLATFORM_TEAM', 'PLATFORM_ADMIN', 'IT_TEAM', 'IT_ADMIN', 'DATA_TEAM', 'DATA_ADMIN'];
  }

  if (currentRole === 'IT_ADMIN') {
    return ['IT_TEAM', 'IT_ADMIN'];
  }

  if (currentRole === 'DATA_ADMIN') {
    return ['DATA_TEAM', 'DATA_ADMIN'];
  }

  if (currentRole === 'PLATFORM_ADMIN') {
    return ['PLATFORM_TEAM', 'PLATFORM_ADMIN'];
  }

  // Regular team members cannot assign roles
  return [];
}

/**
 * Check if user A can view/manage user B
 * SYSTEM_ADMIN can manage everyone
 * Team admins can only manage their team members
 * Regular team members cannot manage anyone
 * @param managerRole Role of the user trying to manage
 * @param targetRole Role of the user being managed
 * @returns true if manager can manage this user
 */
export function canUserManageRole(managerRole: UserRole, targetRole: UserRole): boolean {
  // SYSTEM_ADMIN can manage everyone
  if (managerRole === 'SYSTEM_ADMIN') {
    return true;
  }

  // Non-admin users cannot manage anyone
  if (!isTeamAdmin(managerRole)) {
    return false;
  }

  // Team admins can only manage their team's members
  const managerTeam = getAdminTeam(managerRole);
  const targetTeam = getTeamFromRole(targetRole);

  // Cannot manage SYSTEM_ADMIN
  if (targetRole === 'SYSTEM_ADMIN') {
    return false;
  }

  // Cannot manage other teams' admins
  if (isTeamAdmin(targetRole) && targetTeam !== managerTeam) {
    return false;
  }

  // Can manage own team
  return managerTeam === targetTeam;
}

/**
 * Check if a role has access to a specific team's work
 * @param role User role
 * @param requiredTeam Required team ('PLATFORM', 'IT', 'DATA')
 * @returns true if user has access
 */
export function hasTeamAccess(role: UserRole, requiredTeam: 'PLATFORM' | 'IT' | 'DATA'): boolean {
  // SYSTEM_ADMIN has access to everything
  if (role === 'SYSTEM_ADMIN') {
    return true;
  }

  const userTeam = getTeamFromRole(role);
  return userTeam === requiredTeam;
}

/**
 * Get the base team role from a role
 * Converts admin roles to their corresponding team role
 * @param role User role
 * @returns Base team role or SYSTEM_ADMIN
 */
export function getBaseTeamRole(role: UserRole): 'PLATFORM_TEAM' | 'IT_TEAM' | 'DATA_TEAM' | 'SYSTEM_ADMIN' {
  switch (role) {
    case 'PLATFORM_TEAM':
    case 'PLATFORM_ADMIN':
      return 'PLATFORM_TEAM';
    case 'IT_TEAM':
    case 'IT_ADMIN':
      return 'IT_TEAM';
    case 'DATA_TEAM':
    case 'DATA_ADMIN':
      return 'DATA_TEAM';
    case 'SYSTEM_ADMIN':
      return 'SYSTEM_ADMIN';
  }
}

/**
 * Get all roles that belong to a specific team
 * @param team Team name
 * @returns Array of roles for this team
 */
export function getRolesForTeam(team: 'PLATFORM' | 'IT' | 'DATA'): UserRole[] {
  switch (team) {
    case 'PLATFORM':
      return ['PLATFORM_TEAM', 'PLATFORM_ADMIN'];
    case 'IT':
      return ['IT_TEAM', 'IT_ADMIN'];
    case 'DATA':
      return ['DATA_TEAM', 'DATA_ADMIN'];
  }
}
